import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Swap.css';

interface ConfirmSwapModalProps {
  isOpen: boolean;
  onClose: () => void;
  fromAmount: string;
  toAmount: string;
  fromSymbol: string;
  toSymbol: string;
  onConfirm: () => void;
}

const ConfirmSwapModal: React.FC<ConfirmSwapModalProps> = ({
  isOpen,
  onClose,
  fromAmount,
  toAmount,
  fromSymbol,
  toSymbol,
  onConfirm
}) => {
  if (!isOpen) return null;

  return (
    <div className="swap-modal-overlay" onClick={onClose}>
      <div className="swap-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="swap-modal-header">
          <h2 className="swap-modal-title">Swap Crypto</h2>
          <button className="swap-modal-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div className="swap-modal-amount-from">
          {fromAmount} {fromSymbol}
        </div>
        
        <div className="swap-modal-swap-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M7 16V4M7 4L3 8M7 4L11 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M17 8V20M17 20L21 16M17 20L13 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        
        <div className="swap-modal-amount-to">
          {toAmount} {toSymbol}
        </div>
        
        <div className="swap-modal-details">
          <div className="swap-modal-detail-row">
            <span className="swap-modal-detail-label">RATE</span>
          </div>
          <div className="swap-modal-detail-row">
            <span className="swap-modal-detail-text">{fromAmount} {fromSymbol}</span>
            <span className="swap-modal-detail-equals">=</span>
            <span className="swap-modal-detail-text">{toAmount}</span>
          </div>
          
          <div className="swap-modal-detail-row swap-modal-detail-row-spaced">
            <span className="swap-modal-detail-label">Payment Method</span>
            <span className="swap-modal-detail-value">Spot</span>
          </div>
          
          <div className="swap-modal-detail-row swap-modal-detail-row-spaced">
            <span className="swap-modal-detail-label">Transaction Fees</span>
            <span className="swap-modal-detail-value swap-modal-detail-value-highlight">No Fees</span>
          </div>
          
          <div className="swap-modal-divider"></div>
          
          <div className="swap-modal-detail-row swap-modal-detail-row-spaced">
            <span className="swap-modal-detail-final-label">You Will Get</span>
            <span className="swap-modal-detail-final-value">{toAmount} {toSymbol}</span>
          </div>
        </div>
        
        <button className="swap-modal-confirm-button" onClick={onConfirm}>
          Confirm (4s)
        </button>
      </div>
    </div>
  );
};

interface Cryptocurrency {
  id: string;
  symbol: string;
  name: string;
  icon: string;
}

const mockCryptos: Cryptocurrency[] = [
  { id: 'usdt', symbol: 'USDT', name: 'Tether', icon: '₮' },
  { id: 'sol', symbol: 'SOL', name: 'Solana', icon: '◎' },
  { id: 'btc', symbol: 'BTC', name: 'Bitcoin', icon: '₿' },
  { id: 'eth', symbol: 'ETH', name: 'Ethereum', icon: 'Ξ' },
  { id: 'bnb', symbol: 'BNB', name: 'BNB', icon: '◆' },
];

const Swap: React.FC = () => {
  const navigate = useNavigate();
  const [fromCrypto, setFromCrypto] = useState(mockCryptos[0]);
  const [toCrypto, setToCrypto] = useState(mockCryptos[1]);
  const [fromAmount, setFromAmount] = useState('100');
  const [toAmount, setToAmount] = useState('0.6534');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleBack = () => {
    navigate(-1);
  };

  const handleBuyCrypto = () => {
    console.log('Navigate to buy crypto');
  };

  const handleHistory = () => {
    navigate('/history-deposit');
  };

  const handleSwapCryptos = () => {
    const temp = fromCrypto;
    setFromCrypto(toCrypto);
    setToCrypto(temp);
    const tempAmount = fromAmount;
    setFromAmount(toAmount);
    setToAmount(tempAmount);
  };

  const handlePreviewConversion = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleConfirmSwap = () => {
    setIsModalOpen(false);
    navigate('/swap-success');
  };

  const fromBalance = '100.00';
  const toBalance = '0.5';
  const minAmount = '0.001';
  const maxAmount = '100.00';

  return (
    <div className="swap-container">
      {/* Header */}
      <header className="swap-header">
        <button className="swap-back-button" onClick={handleBack} aria-label="Go back">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="swap-header-actions">
          <button className="swap-header-button" onClick={handleBuyCrypto}>
            <span className="swap-header-button-icon">+</span>
            <span>Buy Crypto</span>
          </button>
          <button className="swap-header-button" onClick={handleHistory}>
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
              <path d="M5.5 1V5.5L8.25 8.25" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="5.5" cy="5.5" r="4.5" stroke="currentColor" strokeWidth="1.2"/>
            </svg>
            <span>History</span>
          </button>
        </div>
      </header>

      <div className="swap-content">
        {/* Title */}
        <div className="swap-title-section">
          <h1 className="swap-title">Swap</h1>
          <button className="swap-learn-link">
            How to swap crypto? <span className="swap-learn-highlight">Learn more</span>
          </button>
        </div>

        {/* From Section */}
        <div className="swap-card">
          <label className="swap-label">From</label>
          <div className="swap-input-row">
            <button className="swap-crypto-selector">
              <div className="swap-crypto-info">
                <div className="swap-crypto-icon">{fromCrypto.icon}</div>
                <span className="swap-crypto-symbol">{fromCrypto.symbol}</span>
              </div>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="swap-amount-input">
              <input
                type="text"
                value={fromAmount}
                onChange={(e) => setFromAmount(e.target.value)}
                className="swap-input"
              />
            </div>
          </div>
          <div className="swap-info-row">
            <span className="swap-balance">Bal: <span className="swap-balance-value">${fromBalance}</span></span>
          </div>
          <div className="swap-limits-row">
            <span className="swap-limit">Min: <span className="swap-limit-value">{minAmount} {fromCrypto.symbol}</span></span>
            <span className="swap-limit">Max: <span className="swap-limit-max">{maxAmount} {fromCrypto.symbol}</span></span>
          </div>
        </div>

        {/* Swap Button */}
        <button className="swap-toggle-button" onClick={handleSwapCryptos} aria-label="Swap currencies">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M4 12L4 16L8 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 8L16 4L12 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 4L10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M4 16L10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>

        {/* To Section */}
        <div className="swap-card">
          <label className="swap-label">To</label>
          <div className="swap-input-row">
            <button className="swap-crypto-selector">
              <div className="swap-crypto-info">
                <div className="swap-crypto-icon">{toCrypto.icon}</div>
                <span className="swap-crypto-symbol">{toCrypto.symbol}</span>
              </div>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="swap-amount-output">
              <span className="swap-output-value">{toAmount}</span>
              <span className="swap-output-approx">≈</span>
            </div>
          </div>
          <div className="swap-info-row">
            <span className="swap-balance">Balance: {toBalance} {toCrypto.symbol}</span>
          </div>
        </div>

        {/* No Extra Fees */}
        <div className="swap-fees-info">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M5 8L7 10L11 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>No Extra Fees</span>
        </div>

        {/* Preview Conversion Button */}
        <button className="swap-preview-button" onClick={handlePreviewConversion}>
          Preview Conversion
        </button>
      </div>

      <ConfirmSwapModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        fromAmount={fromAmount}
        toAmount={toAmount}
        fromSymbol={fromCrypto.symbol}
        toSymbol={toCrypto.symbol}
        onConfirm={handleConfirmSwap}
      />
    </div>
  );
};

export default Swap;